import React, { FC } from 'react'

import { ArtboardDetailInfoFragment } from '@sketch/gql-types'
import { IS_EMBEDDED } from '@sketch/constants'
import { ShareWithoutVersion, useVersioning } from 'modules/versioning'
import { versionedRoutes } from '@sketch/modules-common'
import { useForDesktop, useForTablet } from '@sketch/components'
import { Zoom, ZoomTypes } from 'components/Zoom'

import { AddAnnotationNavbarItem } from 'modules/annotations/components'
import { usePlaceDraftAnnotation } from 'modules/annotations/hooks'

import HeaderNavigation from '../HeaderNavigation'
import HeaderSidebarToggle from '../HeaderSidebarToggle'
import { EditInSketchButton, OpenTemplateButton } from '../EditInSketchButton'
import VersionLinkButton from '../VersionLinkButton'
import PlayPrototypeLink from '../PlayPrototypeLink'

import {
  NavbarItem,
  MiddleContainer,
  AddAnnotationText,
} from './DetailHeader.styles'
import DetailSettingsDropdown from './DetailSettingsDropdown'
import { DocumentSidebarLayoutExtraProps } from '../DocumentSidebarLayout'
import { useArtboardContext } from 'modules/shares/DetailView/ArtboardContext'

type DetailHeaderProps = {
  share: ShareWithoutVersion
  artboard?: ArtboardDetailInfoFragment
  artboardLatestVersion: string
  isViewingLatestVersion: boolean
  isPrototypePlayEnabled: boolean
  userCanOpenInApp: boolean
  zoom: ZoomTypes
  setZoom: (newZoom: ZoomTypes) => void
} & Pick<
  DocumentSidebarLayoutExtraProps,
  'setSidebarLeftOpen' | 'toggleSidebarRight' | 'isSidebarRightOpen'
>

export const DetailHeader: FC<DetailHeaderProps> = ({
  share,
  artboard,
  toggleSidebarRight,
  isSidebarRightOpen,
  isPrototypePlayEnabled,
  isViewingLatestVersion,
  userCanOpenInApp,
  zoom,
  setZoom,
}) => {
  const { type } = share
  const isDesktop = useForDesktop()
  const isTabletAndBigger = useForTablet()
  const { latestVersionId } = useVersioning()
  const canOpenInApp = userCanOpenInApp && isDesktop && !IS_EMBEDDED

  const [placeDraftAnnotation] = usePlaceDraftAnnotation() || []

  const isTemplate = type === 'TEMPLATE'

  const {
    isInspectorEnabled,
    toggleGrid,
    isGridEnabled,
    toggleLayout,
    isLayoutEnabled,
  } = useArtboardContext()

  const gridAndLayoutPropsForZoomComponent = isInspectorEnabled
    ? {
        toggleGrid,
        toggleLayout,
        isGridEnabled,
        isLayoutEnabled,
      }
    : undefined

  if (placeDraftAnnotation && isTabletAndBigger) {
    return (
      <>
        <MiddleContainer />
        <AddAnnotationText>Click on the Artboard to comment</AddAnnotationText>
        <MiddleContainer>
          <NavbarItem>
            <Zoom
              zoom={zoom}
              setZoom={setZoom}
              gridAndLayout={gridAndLayoutPropsForZoomComponent}
            />
          </NavbarItem>
          <AddAnnotationNavbarItem />
        </MiddleContainer>
        <HeaderSidebarToggle
          toggleSidebar={toggleSidebarRight}
          isSidebarOpen={isSidebarRightOpen}
        />
      </>
    )
  }

  const showLatestVersionButton =
    isTabletAndBigger &&
    !isViewingLatestVersion &&
    !IS_EMBEDDED &&
    // never show if user can only access latest version (it will update automatically)
    share.versionTypeAvailableToUser !== 'LATEST'

  const latestVersionPathname = versionedRoutes.ARTBOARD_DETAIL.VERSION.create({
    permanentArtboardShortId: artboard?.permanentArtboardShortId!,
    shareID: share.identifier,
    versionShortId: latestVersionId,
  })

  return (
    <>
      <HeaderNavigation share={share} artboard={artboard} />

      {isTabletAndBigger && (
        <NavbarItem>
          <Zoom
            zoom={zoom}
            setZoom={setZoom}
            gridAndLayout={gridAndLayoutPropsForZoomComponent}
          />
        </NavbarItem>
      )}

      {canOpenInApp && isTemplate && (
        <NavbarItem>
          <OpenTemplateButton
            shareId={share.identifier}
            disabled={!isViewingLatestVersion}
          />
        </NavbarItem>
      )}

      {canOpenInApp && !isTemplate && (
        <NavbarItem>
          <EditInSketchButton
            shareId={share.identifier}
            disabled={!isViewingLatestVersion}
          />
        </NavbarItem>
      )}

      {artboard && showLatestVersionButton && (
        <NavbarItem>
          <VersionLinkButton to={latestVersionPathname}>
            See Latest Version
          </VersionLinkButton>
        </NavbarItem>
      )}

      {artboard && isTabletAndBigger && artboard.isFlowHome && (
        <NavbarItem>
          <PlayPrototypeLink
            isPrototypePlayEnabled={isPrototypePlayEnabled}
            artboardUUID={artboard.uuid}
            shareIdentifier={share.identifier}
            representation="navbar"
          />
        </NavbarItem>
      )}

      {isTabletAndBigger && <AddAnnotationNavbarItem />}
      <DetailSettingsDropdown />

      <HeaderSidebarToggle
        toggleSidebar={toggleSidebarRight}
        isSidebarOpen={isSidebarRightOpen}
      />
    </>
  )
}

export default DetailHeader
